import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useUserStore } from './userStore';

interface IdeaGenerationStore {
  monthlyLimit: number;
  currentMonthUsage: number;
  lastResetDate: string;
  incrementUsage: () => boolean;
  canGenerateIdeas: () => boolean;
  resetMonthlyUsage: () => void;
  getMonthlyLimit: () => number;
}

export const useIdeaGenerationStore = create<IdeaGenerationStore>()(
  persist(
    (set, get) => ({
      monthlyLimit: 5, // Default free plan limit
      currentMonthUsage: 0,
      lastResetDate: new Date().toISOString(),
      
      getMonthlyLimit: () => {
        const user = useUserStore.getState().currentUser;
        if (!user) return 5; // Free plan default
        
        switch (user.role) {
          case 'admin':
            return Infinity;
          case 'premium':
          case 'premium_yearly':
            return 30;
          case 'pro':
          case 'pro_yearly':
            return 100;
          default:
            return 5;
        }
      },
      
      incrementUsage: () => {
        const user = useUserStore.getState().currentUser;
        if (!user) return false;

        // Admin users have unlimited access
        if (user.role === 'admin') return true;

        const { canGenerateIdeas } = get();
        if (!canGenerateIdeas()) {
          return false;
        }

        set(state => ({
          currentMonthUsage: state.currentMonthUsage + 1
        }));
        return true;
      },

      canGenerateIdeas: () => {
        const user = useUserStore.getState().currentUser;
        if (!user) return false;
        
        // Admin users have unlimited access
        if (user.role === 'admin') return true;

        const state = get();
        const now = new Date();
        const lastReset = new Date(state.lastResetDate);

        // Reset if it's a new month
        if (now.getMonth() !== lastReset.getMonth() || now.getFullYear() !== lastReset.getFullYear()) {
          state.resetMonthlyUsage();
          return true;
        }

        const limit = get().getMonthlyLimit();
        return state.currentMonthUsage < limit;
      },

      resetMonthlyUsage: () => set({
        currentMonthUsage: 0,
        lastResetDate: new Date().toISOString()
      })
    }),
    {
      name: 'idea-generation-storage',
      version: 2,
      migrate: (persistedState: any, version: number) => {
        if (version === 1) {
          return {
            ...persistedState,
            monthlyLimit: 5,
            currentMonthUsage: 0,
            lastResetDate: new Date().toISOString()
          };
        }
        return persistedState;
      }
    }
  )
);